import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { keyframes } from 'styled-components';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();

  // Automatically redirect to calendar page after 5 seconds
  useEffect(() => {
    const redirectTimer = setTimeout(() => {
      navigate('/calendar-ai-consultation');
    }, 5000);

    return () => clearTimeout(redirectTimer);
  }, [navigate]);

  return (
    <SuccessContainer>
      <ContentWrapper>
        <CheckmarkContainer>
          <Checkmark>✓</Checkmark>
        </CheckmarkContainer>
        
        <SuccessHeading>Payment Successful!</SuccessHeading>
        
        <SuccessMessage>
          Thank you for purchasing an AI consultation with us. Your payment has been processed successfully.
        </SuccessMessage>
        
        <InstructionBox>
          <InstructionTitle>Next Steps:</InstructionTitle>
          <InstructionList>
            <InstructionItem>You'll be redirected to our scheduling page in a few seconds</InstructionItem>
            <InstructionItem>Book your initial call to discuss the details of your inquiry</InstructionItem>
            <InstructionItem>This is not the final consultation - it's a preliminary discussion</InstructionItem>
            <InstructionItem>The final consultation can be scheduled immediately after the initial call or at any other time you decide</InstructionItem>
          </InstructionList>
        </InstructionBox>
        
        <BookNowButton onClick={() => navigate('/calendar-ai-consultation')}>
          Book Your Call Now
        </BookNowButton>
        
        <RedirectMessage>
          You will be automatically redirected to the scheduling page in a few seconds...
        </RedirectMessage>
      </ContentWrapper>
    </SuccessContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(2, 74, 95, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(2, 74, 95, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(2, 74, 95, 0);
  }
`;

const SuccessContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8f9fa, #e9ecef);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
`;

const ContentWrapper = styled.div`
  max-width: 700px;
  width: 100%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 50px 40px;
  text-align: center;
  animation: ${fadeIn} 0.6s ease forwards;
  
  @media (max-width: 768px) {
    padding: 40px 25px;
  }
`;

const CheckmarkContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #024A5F;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  animation: ${pulse} 2s infinite;
`;

const Checkmark = styled.span`
  color: white;
  font-size: 40px;
  font-weight: bold;
`;

const SuccessHeading = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #024A5F;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SuccessMessage = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const InstructionBox = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
  text-align: left;
  border-left: 4px solid #024A5F;
`;

const InstructionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #024A5F;
  margin-bottom: 15px;
`;

const InstructionList = styled.ul`
  padding-left: 20px;
`;

const InstructionItem = styled.li`
  font-size: 16px;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const BookNowButton = styled.button`
  background: linear-gradient(135deg, #024A5F, #0871A4);
  color: white;
  font-weight: 600;
  font-size: 18px;
  padding: 14px 36px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(2, 74, 95, 0.2);
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(2, 74, 95, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 30px;
  }
`;

const RedirectMessage = styled.p`
  font-size: 15px;
  color: #6c757d;
  font-style: italic;
`;

export default PaymentSuccessPage; 